import type { AuthError, Provider, Session, User } from '@supabase/supabase-js';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { config } from '@/api/config';
import { supabase } from '@/api/supabase';

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signInWithOAuth: (provider: Provider) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<boolean>;
  signInWithPassword: (email: string, password: string) => Promise<boolean>;
  signUp: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  error: AuthError | null;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithOAuth = useCallback(async (provider: Provider) => {
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: config.app.url,
      },
    });
    if (error) setError(error);
  }, []);

  const signInWithMagicLink = useCallback(async (email: string): Promise<boolean> => {
    setError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: config.app.url,
      },
    });
    if (error) {
      setError(error);
      return false;
    }
    return true;
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setError(error);
        return false;
      }
      return true;
    },
    [],
  );

  const signUp = useCallback(async (email: string, password: string): Promise<boolean> => {
    setError(null);

    // Clear previous user flags to ensure new user sees welcome tour
    localStorage.removeItem('banquinha_welcome_tour_completed');
    localStorage.removeItem('banquinha_profile_id');

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: config.app.url,
      },
    });
    if (error) {
      setError(error);
      return false;
    }
    return true;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    const { error } = await supabase.auth.signOut();
    if (error) setError(error);

    // Clear user-specific localStorage flags on logout
    localStorage.removeItem('banquinha_welcome_tour_completed');
    localStorage.removeItem('banquinha_profile_id');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      isLoading,
      isAuthenticated: !!user,
      signInWithOAuth,
      signInWithMagicLink,
      signInWithPassword,
      signUp,
      signOut,
      error,
      clearError,
    }),
    [
      user,
      session,
      isLoading,
      signInWithOAuth,
      signInWithMagicLink,
      signInWithPassword,
      signUp,
      signOut,
      error,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
